import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ArrowLeftOutlined, BranchesOutlined, QuestionCircleOutlined, SelectOutlined, UnorderedListOutlined, ShareAltOutlined, TrophyOutlined } from "@ant-design/icons"
import { Flex, Select, Typography, Avatar, Button, Divider, Layout, message, Dropdown, Modal } from "antd"
import "./header.css"
import { v4 as uuidv4 } from "uuid";

import { useParams } from "react-router-dom"
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { createDatabase, getSingleMasterData } from "../../services/adminServices";
import { showLoader } from "../../utils/showloader";
import { buildMasterBackedWidgetTranslations } from "../../utils/masterTranslations.js";
import { buildMasterColumnOptions } from "../../utils/masterHelper.js";
import { setSelectedWidget } from "../../redux/slices/widgetsSlice";
import WidgetIcon from "../form-editor/WidgetIcon";
import { iconColorBgColor } from "../../utils/colorBgColorMap";
import ConditionManager from "../form-editor/conditionManager";
import { getSpaceAllFormsByWorkflowId } from "../../services/requestServices";
import ShareFormModal from "../form-editor/ShareFormModal";
import ScoringPanel from "../form-editor/ScoringPanel";
import { getFormDefaultValues } from "../../utils/formsHelper";

const { Title } = Typography;
const { Header } = Layout;

const FormEditorHeader = (props) => {
    const { formId, headerData, formElements, masterData, masterList, saveWorkflowForm, conditions, fetchConditions, setPreviewMode, addMasterData, orgStructureData, workflowSteps, defaultValues = {}, currentFormId, onGenerateConditions, updateFieldOptions, validateSidebar } = props
    const { t } = useTranslation();
    const navigate = useTenantNavigation();
    const { spaceId, workflowId, nodeId } = useParams();
    const [currentForm, setCurrentForm] = useState("")
    const [dbData, setDbData] = useState([])
    const [openColumnSelector, setOpenColumnSelector] = useState(false)
    const [selectedMasterColumn, setSelectedMasterColumn] = useState([])
    const [currentDbColumns, setCurrentDbColumns] = useState([])
    const [selectedDb, setSelectedDb] = useState('')
    const [selectedMasterTable, setSelectedMasterTable] = useState(null)
    const [workflowForms, setWorkflowForms] = useState([])
    const [allFormElements, setAllFormElements] = useState([])
    const [showShareModal, setShowShareModal] = useState(false)
    const [showScoringPanel, setShowScoringPanel] = useState(false)
    const dispatch = useDispatch();

    // A questionnaire section anywhere in the form unlocks the scoring panel.
    const hasQuestionnaireSection = (Array.isArray(formElements) ? formElements : []).some(
        (element) => element.formType === "section" && element.isQuestionnaireSection
    )

    const fetchWorkflowForms = async () => {
        showLoader(true)
        let response = await getSpaceAllFormsByWorkflowId(workflowId)
        showLoader(false)
        let spaceForms = []
        if (response.data?.length > 0) {
            spaceForms = response.data.map(form => {
                return { nodeId: form.nodeId, value: form.formId, label: form.formHeader?.label }
            })
        }
        setWorkflowForms(spaceForms)
        let allElements = []
        ;(Array.isArray(response.data) ? response.data : []).forEach(form => {
            if (form.items?.length > 0) {
                let currentFormItems = form.items
                let formHeaderLabel = form.formHeader?.label || ''
                const formItems = currentFormItems?.map(item => {
                    if (item.isTableColumnComputation) {
                        return { ...item, formLabel: formHeaderLabel, tableId: item.calculationField?.tableId, columnId: item.calculationField?.columnId }
                    } else if (item.children) {
                        let sectionItems = item.children.map(child => {
                            if (child.isTableColumnComputation) {
                                return { ...child, formLabel: formHeaderLabel, tableId: child.calculationField?.tableId, columnId: child.calculationField?.columnId }
                            }
                            return { ...child, formLabel: formHeaderLabel }
                        })
                        item.children = sectionItems
                    }
                    return { ...item, formLabel: formHeaderLabel }
                })
                allElements = [...allElements, ...formItems]
            }
        })
        setAllFormElements(allElements)

    }

    useEffect(() => {
        fetchWorkflowForms();
    }, [])

    useEffect(() => {
        if (headerData.label) {
            setCurrentForm(headerData.label)
        }
    }, [headerData])

    const onSelectDb = async (dbId) => {
        setSelectedDb(dbId)
        let selectedTable = masterList.find(data => data.dataId === dbId);
        if (!selectedTable) {
            selectedTable = orgStructureData.find(data => data.label === dbId)
            let strItem = { ...selectedTable, formType: "dropdownField", id: uuidv4() }
            addMasterData(strItem)
            setCurrentDbColumns([])
            handleSelectorClose()
        } else {
            // The list payload can omit field/row translations. Load the full table
            // so added form fields keep the column's label and option translations.
            showLoader(true)
            const response = await getSingleMasterData(dbId)
            showLoader(false)
            const fullTable = Array.isArray(response?.data?.fields) ? response.data : selectedTable
            setSelectedDb(fullTable.dataId || dbId)
            setCurrentDbColumns(fullTable?.fields || [])
            setSelectedMasterTable(fullTable)
            setOpenColumnSelector(true)
        }
    }

    useEffect(() => {
        if (masterList.length > 0) {
            let updatedList = masterList.filter(data => (data.fields && data.fields.length > 0) && data.dataName !== 'CONFIGURATION');
            updatedList = updatedList.map(data => {
                return { label: <div onClick={() => onSelectDb(data.dataId)}>{data.dataName}</div>, value: data.dataId, key: data.dataId, }
            })
            let strData = orgStructureData.map(data => {
                return { label: <div onClick={() => onSelectDb(data.label)}>{data.label} (Org Structure)</div>, value: data.label, key: data.label, }

            })
            updatedList = strData.concat(updatedList)
            setDbData(updatedList)
        }
    }, [masterList, orgStructureData])

    const handleFormPreview = () => {
        setPreviewMode(true)
    }

    const goBack = () => {
        navigate(-1);
    }

    const deleteIconFromItemRecursively = (items) => {
        return items.map(item => {
            delete item.icon
            if (item.children) {
                deleteIconFromItemRecursively(item.children)
            }
            return { ...item }
        })
    }

    const saveFormData = async () => {
        // Validate that every form item has a label before attempting to save
        const flatten = (items = []) => {
            let flat = [];
            items.forEach(item => {
                const { children, ...rest } = item || {};
                flat.push({ ...rest });
                if (Array.isArray(item.children) && item.children.length > 0) {
                    flat = flat.concat(flatten(item.children));
                }
            });
            return flat;
        };
        const flattened = flatten(formElements || []);
        const missing = flattened.filter(i => !i.label || String(i.label).trim() === "");
        if (missing.length > 0) {
            message.error(`Please provide labels for all form items. ${missing.length} item(s) missing.`);
            // Focus the first missing element in the editor by dispatching selection
            try {
                // Pick the first missing item
                const firstMissing = missing[0];
                // Dispatch a clean object similar to widget selection
                dispatch(setSelectedWidget({ ...firstMissing, _selectionTime: Date.now() }));
                // Wait a tick for the DOM to update and then scroll the selected element into view
                setTimeout(() => {
                    const sel = document.querySelector('.selected-element');
                    if (sel && typeof sel.scrollIntoView === 'function') {
                        sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
            } catch (err) {
                // ignore dispatch/scroll errors
            }
            return;
        }
        if (validateSidebar && typeof validateSidebar === 'function') {
            let sidebarValid = false;
            try {
                sidebarValid = await validateSidebar();
            } catch (err) {
                sidebarValid = false;
            }
            if (!sidebarValid) {
                message.error(t('Please fix errors in the selected widget panel before saving.'));
                return;
            }
        }
        if (masterData?.dataName) {
            let updatedFields = []
            formElements.forEach(element => {
                let postObj = { ...element, readOnly: element.readOnly || false, tooltip: element.tooltip || false, helpText: element.helpText || "", mappedColumn: element.mappedColumn || "", isMasterData: element.isMasterData || false }

                if (element.children) {
                    postObj.children = element.children
                }
                if (element.parentTable) {
                    postObj.parentTable = element.parentTable
                }
                updatedFields.push(postObj)
                if (element.options) {
                    postObj.options = element.options
                }
            })
            let postData = { dataId: masterData.dataId, "dataName": headerData.label, fields: updatedFields, data: [] }
            let formDefaultValues = getFormDefaultValues(defaultValues)
            postData.defaultValues = formDefaultValues
            // The header widget holds the table name as `label`; a master table carries
            // it as `dataName`, so translate the key on the way out. `dataName` itself
            // stays canonical — it is the identifier used in URLs and lookups.
            if (headerData?.translations) {
                postData.translations = Object.entries(headerData.translations).reduce((acc, [lng, bucket]) => {
                    if (typeof bucket?.label === "string") {
                        acc[lng] = { dataName: bucket.label }
                    }
                    return acc
                }, {})
            }

            // return
            showLoader(true)
            const updateForm = await createDatabase(postData, true)
            showLoader(false)
            if (updateForm.status === 200) {
                message.success(t('Settings updated'));
                navigate("/app/admin/master-data/tables/" + masterData.dataId + "?table=" + headerData.label)
            } else {
                message.error(updateForm.message)
            }
        }
        if (nodeId) {
            let updatedFormelements = formElements.map(ele => {
                if (ele.isTableColumnComputation) {
                    ele.calculationField = { tableId: ele.tableId, columnId: ele.columnId }
                } else if (ele.children?.length > 0) {
                    let sectionItems = ele.children.map(child => {
                        if (child.isTableColumnComputation) {
                            return { ...child, calculationField: { tableId: child.tableId, columnId: child.columnId } }
                        }
                        return { ...child }
                    })
                    ele.children = sectionItems
                }
                return { ...ele }
            })
            let postData = { spaceId: spaceId, nodeId: nodeId, workflowId: workflowId, items: updatedFormelements }
            let formDefaultValues = getFormDefaultValues(defaultValues)
            postData.defaultValues = formDefaultValues

            // return
            const saved = await saveWorkflowForm(postData)
            if (saved) {
                fetchWorkflowForms()
            }
        }
    }

    const handleColumnSelection = async () => {
        if (!selectedDb) {
            return
        }
        let addedItems = []
        const selectedDbObj = selectedMasterTable
            || masterList.find(data => data.dataId === selectedDb)
        if (!selectedDbObj) {
            return
        }
        const referencedIds = [...new Set((selectedDbObj.fields || [])
            .filter((field) => selectedMasterColumn.includes(field?.label) && field?.parentTable)
            .map((field) => field.parentTable)
            .filter((id) => id && !masterList.some((master) => master?.dataId === id)))]
        let extraMasters = []
        if (referencedIds.length) {
            showLoader(true)
            const responses = await Promise.all(referencedIds.map((id) => getSingleMasterData(id)))
            showLoader(false)
            extraMasters = responses.map((response) => response?.data).filter((master) => master?.dataId)
        }
        const listForResolve = [...(Array.isArray(masterList) ? masterList : []), selectedDbObj, ...extraMasters]
        selectedMasterColumn.forEach(val => {
            let fieldFromDb = (selectedDbObj.fields || []).find(field => field.label === val)
            if (!fieldFromDb) {
                return
            }
            const { translations: _fieldTranslations, ...fieldRest } = fieldFromDb
            const columnOptions = buildMasterColumnOptions(selectedDbObj, val, listForResolve)
            let widgetObj = {
                ...fieldRest,
                mappedColumn: val,
                parentTable: selectedDb,
                formType: "dropdownField",
                id: uuidv4(),
                options: columnOptions,
                isMasterData: true,
                fieldId: selectedDb,
            }
            const translations = buildMasterBackedWidgetTranslations(
                fieldFromDb,
                selectedDbObj,
                val,
                columnOptions,
                listForResolve
            )
            if (translations) {
                widgetObj.translations = translations
            }

            addedItems.push({ ...widgetObj })
        })

        addMasterData(addedItems)
        setCurrentDbColumns([])
        handleSelectorClose()
    }

    const handleSelectorClose = () => {
        setOpenColumnSelector(false)
        setSelectedMasterColumn([])
        setSelectedMasterTable(null)
    }

    const handleFormChange = (val) => {
        setCurrentForm(val)
        let reqForm = workflowForms.find(form => form.value === val)
        if (reqForm) {
            let navPath = "/app/space/" + spaceId + "/" + workflowId + "/" + reqForm.nodeId + "/form-editor/" + val
            navigate(navPath, { replace: true })
        }
    }

    const showConditionManager = () => {
        let containerEle = document.getElementById("condition-container");
        if (containerEle) {
            containerEle.style.display = "block";
        }
    }

    const saveConditions = () => {
    }

    return (
        <Header className="app-header">
            <Flex className="app-header-left">
                <ArrowLeftOutlined className="back-icon" onClick={goBack} />
                <Title level={5} className='section-title' style={{ margin: "0" }}>{t("Form Editor")}</Title>
                <Divider type="vertical" style={{ height: "4rem" }} />
                <Select className="form-selector" options={workflowForms}
                    value={currentForm}
                    onChange={handleFormChange}
                />
            </Flex>

            <Flex className="app-header-right" justify="flex-end" align="center">
                {/* <Avatar icon={<QuestionCircleOutlined />} className="icon-style" /> */}
                <Dropdown menu={{ items: dbData }} trigger={"click"}>
                    <Button className="default-btn">
                        <Flex align="center">
                            <img src="/assets/DbConnect.svg" style={{ marginRight: "5px" }} />
                            <div>{t("Connect Master Data")}</div>
                        </Flex>
                    </Button>
                </Dropdown>
                {
                    !masterData.dataName &&
                    <>
                        <Button className="default-btn" icon={<BranchesOutlined />} onClick={showConditionManager}>{t("Form Conditions")}</Button>
                        <Button className="default-btn" icon={<SelectOutlined />} onClick={handleFormPreview}>{t("Preview")}</Button>
                        {hasQuestionnaireSection && (
                            <Button className="default-btn" icon={<TrophyOutlined />} onClick={() => setShowScoringPanel(true)}>{t("Scoring Panel")}</Button>
                        )}
                        {formId && formId !== "new" && (
                            <Button className="default-btn"
                                icon={<ShareAltOutlined />}
                                onClick={() => setShowShareModal(true)}
                            >
                                {t("Share")}
                            </Button>
                        )}
                    </>
                }
                <Button className="success-button" onClick={saveFormData}>{t("Save")}</Button>
            </Flex>
            <ConditionManager
                workflowSteps={workflowSteps}
                formId={currentFormId}
                conditions={conditions}
                fetchConditions={fetchConditions}
                formElements={allFormElements}
                saveConditions={saveConditions}
                onGenerateConditions={onGenerateConditions}
            />

            <ScoringPanel
                open={showScoringPanel}
                onClose={() => setShowScoringPanel(false)}
                formElements={formElements}
                updateFieldOptions={updateFieldOptions}
            />

            <ShareFormModal
                visible={showShareModal}
                onCancel={() => setShowShareModal(false)}
                formId={formId}
                formName={headerData.label || 'Untitled Form'}
                spaceId={spaceId}
            />

            <Modal className="master-data-selector" title={t("Select column")} open={openColumnSelector} footer={false} onOk={handleSelectorClose} onCancel={handleSelectorClose}>
                <Select mode="multiple" style={{ width: "100%" }} value={selectedMasterColumn} placeholder={t("Select column")} options={currentDbColumns.map(field => {
                    return { label: field.label, value: field.label }
                })} onChange={(val) => setSelectedMasterColumn(val)} />
                <Flex justify="flex-end" style={{ marginTop: "16px" }}>
                    <Button type="primary" className="submit-button" onClick={handleColumnSelection}>{t("Add")}</Button>
                </Flex>
            </Modal>
        </Header>
    )
}

export default FormEditorHeader
